/**
 * SourceDocViewer — renders the original source IO document in the left pane
 * of the ReviewPane (PRD §9.2: side-by-side source + extracted fields).
 *
 * Supports:
 *   - PDF  → PDF.js-based viewer with page navigation, zoom, scroll
 *   - Image → direct <img> rendering with zoom controls
 *   - HTML  → sandboxed <iframe> rendering
 *   - CSV   → table renderer (parsed from raw text)
 *   - DOCX/XLSX → text/table fallback showing extracted content if available
 *                (the parser service already extracted sections — we show
 *                 those when raw bytes are not available inline)
 *   - Unknown → download link
 *
 * Local upload-only IOs embed the original file as a data: URL in
 * `io.sourceDocDataUrl`. Backend-served IOs use `/uploads/<file>` which
 * will resolve once a real upload store is wired up.
 */

import React, { useEffect, useRef, useState } from "react";

// Lazy-load PDF.js only when a PDF is actually rendered.
let _pdfjsLib = null;
async function getPdfjsLib() {
  if (_pdfjsLib) return _pdfjsLib;
  const pdfjsLib = await import("pdfjs-dist");
  // Use the worker bundled with pdfjs-dist via Vite's ?url import. Falls
  // back to the unpkg-hosted worker if the bundler doesn't resolve ?url.
  try {
    const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
  } catch {
    pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@4.7.76/build/pdf.worker.min.mjs";
  }
  _pdfjsLib = pdfjsLib;
  return pdfjsLib;
}

const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "webp", "tiff", "tif", "bmp"]);
const PDF_EXTS = new Set(["pdf"]);
const HTML_EXTS = new Set(["html", "htm"]);
const CSV_EXTS = new Set(["csv"]);

function getExt(name) {
  return (name || "").split(".").pop()?.toLowerCase() || "";
}

function getDocUrl(io) {
  const inlineDataUrl = io?.sourceDocDataUrl || null;
  const sourceFile = io?.sourceFile || io?._meta?.source_file || "";
  return inlineDataUrl || (sourceFile ? `/uploads/${encodeURIComponent(sourceFile)}` : null);
}

// ---------------------------------------------------------------------------
// PDF viewer — uses PDF.js with page navigation + zoom
// ---------------------------------------------------------------------------

function PdfViewer({ url, filename }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const renderTaskRef = useRef(null);

  // Load PDF document.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPdf(null);
    setPageNum(1);

    (async () => {
      try {
        const pdfjsLib = await getPdfjsLib();
        const loadingTask = pdfjsLib.getDocument({ url });
        const loadedPdf = await loadingTask.promise;
        if (cancelled) return;
        setPdf(loadedPdf);
        setNumPages(loadedPdf.numPages);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || "Failed to load PDF");
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [url]);

  // Render current page whenever pdf/pageNum/scale changes.
  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;

    // Cancel any in-flight render before starting a new one.
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
    }

    (async () => {
      try {
        const page = await pdf.getPage(pageNum);
        if (cancelled) return;

        const dpr = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: scale * dpr });
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width / dpr}px`;
        canvas.style.height = `${viewport.height / dpr}px`;

        const ctx = canvas.getContext("2d");
        const renderTask = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = renderTask;
        await renderTask.promise;
        if (cancelled) return;
        renderTaskRef.current = null;
      } catch (err) {
        if (err.name !== "RenderingCancelledException") {
          console.error("[PdfViewer] render error:", err);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [pdf, pageNum, scale]);

  function goPrev() { setPageNum((p) => Math.max(1, p - 1)); }
  function goNext() { setPageNum((p) => Math.min(numPages, p + 1)); }
  function zoomIn() { setScale((s) => Math.min(3, +(s + 0.25).toFixed(2))); }
  function zoomOut() { setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2))); }
  function zoomReset() { setScale(1.25); }

  if (error) {
    return (
      <div className="source-doc-fallback">
        <p>Failed to render PDF: {error}</p>
        <a className="btn" href={url} target="_blank" rel="noreferrer">Open in new tab</a>
      </div>
    );
  }

  return (
    <div className="pdf-viewer">
      <div className="pdf-toolbar">
        <button className="btn-icon" onClick={goPrev} disabled={pageNum <= 1 || loading} title="Previous page">‹</button>
        <span className="pdf-page-label">
          {loading ? "Loading…" : `Page ${pageNum} / ${numPages || "?"}`}
        </span>
        <button className="btn-icon" onClick={goNext} disabled={pageNum >= numPages || loading} title="Next page">›</button>
        <span className="pdf-toolbar-sep" />
        <button className="btn-icon" onClick={zoomOut} disabled={scale <= 0.5} title="Zoom out">−</button>
        <span className="pdf-zoom-label">{Math.round(scale * 100)}%</span>
        <button className="btn-icon" onClick={zoomIn} disabled={scale >= 3} title="Zoom in">+</button>
        <button className="btn-icon" onClick={zoomReset} title="Reset zoom">⌂</button>
        <span className="pdf-toolbar-sep" />
        <a className="btn-icon" href={url} target="_blank" rel="noreferrer" title="Open in new tab">↗</a>
      </div>
      <div className="pdf-canvas-wrap" ref={containerRef}>
        {loading && <div className="pdf-loading">Loading PDF…</div>}
        <canvas ref={canvasRef} className="pdf-canvas" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSV viewer — parses and renders as a table
// ---------------------------------------------------------------------------

function parseCsv(text) {
  // Minimal RFC 4180 parser. Handles quoted fields, commas inside quotes,
  // and embedded newlines.
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += c;
      }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n" || c === "\r") {
        row.push(field); field = "";
        if (row.length > 1 || row[0] !== "") rows.push(row);
        row = [];
        if (c === "\r" && text[i + 1] === "\n") i++;
      } else {
        field += c;
      }
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

function CsvViewer({ url }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const text = await resp.text();
        if (cancelled) return;
        setRows(parseCsv(text));
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (error) {
    return (
      <div className="source-doc-fallback">
        <p>Failed to load CSV: {error}</p>
        <a className="btn" href={url} target="_blank" rel="noreferrer">Open in new tab</a>
      </div>
    );
  }

  if (!rows) return <div className="pdf-loading">Loading CSV…</div>;
  if (rows.length === 0) return <div className="source-doc-fallback"><p>CSV is empty.</p></div>;

  const header = rows[0];
  const body = rows.slice(1);
  const lowerSearch = search.toLowerCase();
  const filtered = search
    ? body.filter((row) => row.some((cell) => cell.toLowerCase().includes(lowerSearch)))
    : body;

  return (
    <div className="csv-viewer">
      <div className="csv-toolbar">
        <input
          type="search"
          placeholder="Filter rows…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="csv-search"
        />
        <span className="csv-count">{filtered.length} / {body.length} rows</span>
        <a className="btn-icon" href={url} target="_blank" rel="noreferrer" title="Open in new tab">↗</a>
      </div>
      <div className="csv-table-wrap">
        <table className="csv-table">
          <thead>
            <tr>{header.map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => <td key={ci}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Image viewer — direct <img> rendering with zoom controls
// ---------------------------------------------------------------------------

function ImageViewer({ url, filename }) {
  const [scale, setScale] = useState(1);
  return (
    <div className="image-viewer">
      <div className="image-toolbar">
        <button className="btn-icon" onClick={() => setScale((s) => Math.max(0.25, +(s - 0.25).toFixed(2)))} title="Zoom out">−</button>
        <span className="image-zoom-label">{Math.round(scale * 100)}%</span>
        <button className="btn-icon" onClick={() => setScale((s) => Math.min(4, +(s + 0.25).toFixed(2)))} title="Zoom in">+</button>
        <button className="btn-icon" onClick={() => setScale(1)} title="Reset zoom">⌂</button>
        <a className="btn-icon" href={url} target="_blank" rel="noreferrer" download={filename} title="Open in new tab">↗</a>
      </div>
      <div className="image-canvas-wrap">
        <img
          src={url}
          alt={filename || "Original scanned/handwritten IO"}
          className="image-canvas"
          style={{ transform: `scale(${scale})`, transformOrigin: "top center" }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HTML viewer — sandboxed <iframe>
// ---------------------------------------------------------------------------

function HtmlViewer({ url, filename }) {
  return (
    <div className="source-doc">
      <div className="source-doc-bar">
        <span className="source-doc-name">{filename}</span>
        <a href={url} target="_blank" rel="noreferrer">Open in new tab</a>
      </div>
      <iframe
        src={url}
        title={filename}
        sandbox="allow-same-origin"
        className="source-doc-iframe"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DOCX/XLSX text fallback — shows parser-extracted sections if available
// ---------------------------------------------------------------------------

function OfficeDocViewer({ io, ext }) {
  // The parser service extracts structured text/table sections. These are
  // passed through from the upload route and stored on the IO record in
  // localStorage. Show them inline so the reviewer can see the source content
  // alongside the extracted fields, even though the browser can't natively
  // embed Office documents.
  const sections = io?.extractedSections || io?._meta?.extractedSections || [];
  const url = getDocUrl(io);

  return (
    <div className="source-doc source-doc-office">
      <div className="source-doc-bar">
        <span className="source-doc-name">{io?.sourceFile || io?._meta?.source_file || `${ext.toUpperCase()} document`}</span>
        {url && <a href={url} target="_blank" rel="noreferrer" download={io?.sourceFile || undefined}>Download original</a>}
      </div>
      {sections.length > 0 ? (
        <div className="extracted-sections">
          <p className="muted" style={{ marginBottom: "1em" }}>
            Showing parsed content from the {ext.toUpperCase()} document. {url && (
              <a href={url} target="_blank" rel="noreferrer" download={io?.sourceFile || undefined}>Download the original file</a>
            )} to see the exact formatting.
          </p>
          {sections.map((s, i) => (
            <div key={i} className="extracted-section">
              {s.type === "table" ? (
                <TableSection section={s} />
              ) : (
                <TextSection content={s.content} />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="source-doc-empty">
          <p className="muted">
            No extracted content available. {url && (
              <a href={url} target="_blank" rel="noreferrer" download={io?.sourceFile || undefined}>Download the original file</a>
            )} to view it.
          </p>
        </div>
      )}
    </div>
  );
}

// Render a text section — preserves line breaks and whitespace
function TextSection({ content }) {
  return <pre className="office-text-section">{content}</pre>;
}

// Render a table section — parses pipe-delimited content into an HTML table
function TableSection({ section }) {
  // If table_data is available, use it directly; otherwise parse from content
  let rows = section.table_data;
  if (!rows && section.content) {
    rows = section.content
      .split("\n")
      .filter((line) => line.trim().startsWith("|"))
      .map((line) => {
        const cells = line.split("|").map((c) => c.trim());
        // Remove empty leading/trailing cells from pipe delimiters
        if (cells[0] === "") cells.shift();
        if (cells[cells.length - 1] === "") cells.pop();
        return cells;
      });
  }

  if (!rows || rows.length === 0) {
    return <pre className="mono">{section.content}</pre>;
  }

  // First row is the header
  const header = rows[0];
  const body = rows.slice(1);

  // Detect TOTALS row (empty first cell + "TOTALS" in any cell)
  const isTotalsRow = (row) =>
    row.length > 0 && row[0].trim() === "" &&
    row.some((c) => c.toUpperCase().includes("TOTALS"));

  return (
    <table className="office-table">
      <thead>
        <tr>{header.map((h, i) => <th key={i}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {body.map((row, ri) => (
          <tr key={ri} className={isTotalsRow(row) ? "totals-row" : ""}>
            {row.map((cell, ci) => <td key={ci}>{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Main component — routes to the right viewer
// ---------------------------------------------------------------------------

export default function SourceDocViewer({ io }) {
  const meta = io?._meta || {};
  const sourceFile = io?.sourceFile || meta.source_file || "";
  const ext = getExt(sourceFile);
  const url = getDocUrl(io);

  if (!sourceFile && !io?.sourceDocDataUrl) {
    return (
      <div className="source-doc source-doc-empty">
        <p>No source document attached to this IO.</p>
      </div>
    );
  }

  if (IMAGE_EXTS.has(ext) && url) {
    return (
      <div className="source-doc">
        <div className="source-doc-bar">
          <span className="source-doc-name">{sourceFile || "Original image"}</span>
        </div>
        <ImageViewer url={url} filename={sourceFile} />
      </div>
    );
  }

  if (PDF_EXTS.has(ext) && url) {
    return (
      <div className="source-doc">
        <div className="source-doc-bar">
          <span className="source-doc-name">{sourceFile}</span>
        </div>
        <PdfViewer url={url} filename={sourceFile} />
      </div>
    );
  }

  if (HTML_EXTS.has(ext) && url) {
    return <HtmlViewer url={url} filename={sourceFile} />;
  }

  if (CSV_EXTS.has(ext) && url) {
    return (
      <div className="source-doc">
        <div className="source-doc-bar">
          <span className="source-doc-name">{sourceFile}</span>
        </div>
        <CsvViewer url={url} />
      </div>
    );
  }

  if (["docx", "xlsx"].includes(ext)) {
    return <OfficeDocViewer io={io} ext={ext} />;
  }

  return (
    <div className="source-doc source-doc-empty">
      <p>Source document: <strong>{sourceFile || "unknown"}</strong></p>
      <p className="muted">Preview not available for {ext || "this"} file type.</p>
      {url && (
        <a className="btn" href={url} target="_blank" rel="noreferrer" download={sourceFile || undefined}>
          Download original
        </a>
      )}
    </div>
  );
}